import { motion } from 'framer-motion';
import { Flame, Moon, Brain, Heart, CloudRain, Frown } from 'lucide-react';

const stats = [
  { value: '30M+', label: 'mulheres na menopausa no Brasil' },
  { value: '75%', label: 'sofrem com ondas de calor' },
  { value: '80%', label: 'relatam problemas de sono' },
  { value: '1 em 4', label: 'busca tratamento adequado' },
];

const symptoms = [
  { icon: Flame, label: 'Ondas de calor', color: 'text-red-400 bg-red-50' },
  { icon: Moon, label: 'Insônia', color: 'text-indigo-400 bg-indigo-50' },
  { icon: Brain, label: 'Névoa mental', color: 'text-[#B54182] bg-[#FAADC7]/20' },
  { icon: Frown, label: 'Alterações de humor', color: 'text-amber-400 bg-amber-50' },
  { icon: Heart, label: 'Palpitações', color: 'text-[#BE5791] bg-[#FAADC7]/15' },
  { icon: CloudRain, label: 'Suores noturnos', color: 'text-cyan-400 bg-cyan-50' },
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function PainPointsSection() {
  return (
    <section
      id="sobre"
      data-testid="pain-points-section"
      className="py-24 md:py-32 relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FAADC7] to-transparent" />

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-sm font-semibold text-[#B54182] uppercase tracking-widest mb-4 block">
            Você não está sozinha
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 mb-6"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            A menopausa é real. E{' '}
            <span className="text-[#B54182]">merece atenção.</span>
          </h2>
          <p className="text-base md:text-lg text-stone-500 leading-relaxed">
            Mais de 30 milhões de brasileiras vivenciam a menopausa. Mesmo assim, a maioria enfrenta essa fase sem informação e sem apoio adequado.
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20"
        >
          {stats.map((stat) => (
            <motion.div
              key={stat.label}
              variants={staggerItem}
              className="bg-white rounded-3xl p-6 border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] text-center group hover:shadow-[0_8px_30px_rgba(181,65,130,0.08)] transition-all duration-300"
            >
              <div className="text-3xl md:text-4xl font-bold text-[#B54182] mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                {stat.value}
              </div>
              <p className="text-sm text-stone-500">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Symptoms */}
        <motion.div {...fadeUp} className="text-center mb-10">
          <h3
            className="text-2xl sm:text-3xl font-bold text-stone-900 mb-4"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Você se identifica?
          </h3>
          <p className="text-stone-500 max-w-lg mx-auto">
            Esses são alguns dos sintomas mais comuns da menopausa que o MenoEase ajuda você a rastrear e entender.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
        >
          {symptoms.map((symptom) => {
            const Icon = symptom.icon;
            return (
              <motion.div
                key={symptom.label}
                variants={staggerItem}
                whileHover={{ y: -5, scale: 1.02 }}
                className="bg-white rounded-2xl p-5 border border-stone-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col items-center gap-3 cursor-default"
              >
                <div className={`w-12 h-12 rounded-xl ${symptom.color} flex items-center justify-center`}>
                  <Icon size={22} />
                </div>
                <span className="text-sm font-medium text-stone-700 text-center">{symptom.label}</span>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
